import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Platform, StyleProp, StyleSheet, Text, View, ViewStyle } from "react-native";
import DropDownPicker from "react-native-dropdown-picker";
import { CustomDropdownWeb } from "./CustomDropdownWeb";

export type CustomDropdownOption = {
  label: string;
  value: string;
};

interface CustomDropdownProps {
  options: CustomDropdownOption[];
  value: string | undefined | null;
  onChange: (value: string) => void;
  placeholder?: string;
  placeholderTextColor?: string;
  error?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  openUpward?: boolean;
  accessibilityLabel?: string;
  containerStyle?: StyleProp<ViewStyle>;
  dropdownStyle?: StyleProp<ViewStyle>;
  dropdownContainerStyle?: StyleProp<ViewStyle>;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  showArrow?: boolean;
  hideItemBorders?: boolean;
}

const CustomDropdown = ({
  options,
  value,
  onChange,
  placeholder = "Select an option",
  placeholderTextColor,
  error,
  disabled = false,
  fullWidth = true,
  openUpward = false,
  accessibilityLabel,
  containerStyle,
  dropdownStyle,
  dropdownContainerStyle,
  open: controlledOpen,
  onOpenChange,
  showArrow = true,
  hideItemBorders = false,
}: CustomDropdownProps) => {
  const isWeb = Platform.OS === "web";

  const [open, setOpen] = useState(false);
  const [internalValue, setInternalValue] = useState<string | null>(value ?? null);

  useEffect(() => {
    setInternalValue(value ?? null);
  }, [value]);

  // Parents often pass inline arrays; keep a stable reference when contents are unchanged
  // to avoid dropdown-picker effects firing on every render.
  const stableOptionsRef = useRef<CustomDropdownOption[] | null>(null);
  const stableOptions = useMemo(() => {
    const prev = stableOptionsRef.current;
    if (
      prev &&
      prev.length === options.length &&
      prev.every((p, idx) => p.value === options[idx]?.value && p.label === options[idx]?.label)
    ) {
      return prev;
    }
    stableOptionsRef.current = options;
    return options;
  }, [options]);

  const items = useMemo(() => stableOptions.map((o) => ({ label: o.label, value: o.value })), [stableOptions]);

  const handleSetValue = useCallback((cb) => {
    setInternalValue((prev) => cb(prev));
  }, []);

  const isControlled = controlledOpen != null;
  const openState = isControlled ? (controlledOpen as boolean) : open;

  const setOpenState = useCallback(
    (next: boolean) => {
      if (!isControlled) {
        setOpen(next);
      }
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange]
  );

  if (isWeb) {
    return (
      <CustomDropdownWeb
        options={stableOptions}
        value={value ?? undefined}
        onChange={onChange}
        style={containerStyle}
        error={error}
        disabled={disabled}
        fullWidth={fullWidth}
        placeholder={placeholder}
        placeholderTextColor={placeholderTextColor}
        openUpward={openUpward}
        accessibilityLabel={accessibilityLabel || placeholder}
        open={openState}
        onOpenChange={setOpenState}
        dropdownStyle={dropdownStyle}
        dropdownContainerStyle={dropdownContainerStyle}
        showArrow={showArrow}
        hideItemBorders={hideItemBorders}
      />
    );
  }

  return (
    <View
      style={[
        styles.container,
        fullWidth ? styles.containerFullWidth : styles.containerFitContent,
        openState ? styles.containerOpen : null,
        containerStyle,
      ]}>
      <DropDownPicker
        aria-label={accessibilityLabel || placeholder}
        open={openState}
        value={internalValue}
        items={items}
        setOpen={(next) => {
          const resolved = typeof next === "function" ? next(openState) : next;
          setOpenState(resolved);
        }}
        setValue={handleSetValue}
        disabled={disabled}
        placeholder={placeholder}
        style={[
          styles.dropdownField,
          fullWidth ? styles.dropdownFullWidth : styles.dropdownFitContent,
          dropdownStyle,
          error ? styles.dropdownFieldError : null,
        ]}
        dropDownContainerStyle={[styles.dropdownContainer, dropdownContainerStyle]}
        textStyle={styles.dropdownText}
        onSelectItem={(item) => {
          setOpenState(false);
          if (item?.value != null) {
            onChange(String(item.value));
          }
        }}
        onClose={() => setOpenState(false)}
        dropDownDirection={openUpward ? "TOP" : "BOTTOM"}
        zIndex={100000}
        zIndexInverse={100000}
      />
      {error && error.length > 0 ? <Text style={styles.errorText}>{error}</Text> : null}
    </View>
  );
};

export default CustomDropdown;

const styles = StyleSheet.create({
  container: {
    zIndex: 0,
  },
  containerFullWidth: {
    width: "100%",
  },
  containerFitContent: {
    alignSelf: "flex-start",
  },
  containerOpen: {
    zIndex: 100000,
    elevation: 100000,
  },
  dropdownContainer: {
    borderColor: "rgb(229, 231, 235)",
    backgroundColor: "white",
    zIndex: 100000,
    elevation: 100000,
  },
  dropdownField: {
    borderColor: "rgb(229, 231, 235)",
    zIndex: 100000,
    elevation: 100000,
    minHeight: 42.5,
    backgroundColor: "white",
    borderRadius: 8,
    borderWidth: 1,
  },
  dropdownFullWidth: {
    width: "100%",
  },
  dropdownFitContent: {
    alignSelf: "flex-start",
  },
  dropdownFieldError: {
    borderColor: "#ef4444",
  },
  dropdownText: {
    fontFamily: "Inter",
    fontSize: 14,
    fontWeight: "500",
    color: "#344054",
  },
  errorText: {
    color: "#ef4444",
    fontSize: 12,
    marginTop: 4,
    marginLeft: 8,
  },
});
